import { Card, CardContent, CardHeader } from "@components/common/card"

import { ChevronLeft, ChevronRight, File } from "lucide-react"
import { Link } from "react-router-dom"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Fragment } from "react"
import { DictType, FileItems } from "@pma/types"
import DeleteFile from "../dashboard/delete-file"
import { FilePreview } from "../dashboard/preview-file"
import { Button } from "@components/common/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import RestoreFile from "./restore-file"
import useTranslator from "@hooks/use-translator"
import { formatDate, formatDate_he } from "@pma/utils/helpers"
import { RenderFileIcon } from "./files-view"
import { useAuth } from "@pma/stores/auth-store"

interface Props {
  file: FileItems
}
export const FileCard = ({ file }: Props) => {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const isHebrew = locale === "he"

  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const handleFileType = (file: DictType) => {
    const fileName = file?.name.replace(/\.[^/.]+$/, "").toLowerCase()
    if (fileName == file?.type?.toLowerCase()) return "txt"
    else if (file?.type == "") return "txt"
    else return file?.type
  }

  return (
    <Card className='w-full'>
      <CardHeader className='flex w-full flex-row justify-between p-1.5'>
        <div className='flex max-w-[80%] items-center truncate'>
          {file.path?.map((path: any, index) => (
            <Fragment key={index}>
              <span className='flex items-center text-xs'>{path?.name}</span>
              <span>
                {index !== file?.path.length - 1 && (
                  <>{isHebrew ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}</>
                )}
              </span>
            </Fragment>
          ))}
        </div>

        <div className='flex w-fit items-center'>
          {file?.deleted &&
          (isOrganizationOwner || isAdmin || file?.deleted_by == authConfig.id) ? (
            <RestoreFile file={file} />
          ) : (
            !file?.deleted ?
            <>
              <DeleteFile file={file} />
              <FilePreview file={file} />
            </> : null
          )}
        </div>
      </CardHeader>

      <CardContent className='flex flex-col p-2'>
        <div className='flex items-center'>
          <span className='truncate text-ellipsis text-xs text-slate-500'>
            {translate("Project")}:
          </span>
          <Link to={PROJECTS_PAGE_ROUTE + `/${file?._project?.id}`}>
            <Button variant={"link"} className='text-sm'>
              {file?._project?.title}
            </Button>
          </Link>
        </div>
        <div className='flex items-end justify-between gap-3 py-2'>
          <div className='flex flex-col'>
            <div className='flex h-6 w-6 flex-col items-center justify-center'>
              <RenderFileIcon type={handleFileType(file)} />
            </div>
            <span className='max-w-[12ch] truncate text-ellipsis text-[10px]'>.{file?.type}</span>
          </div>
          <div className='flex flex-col'>
            <span className='text-xs'>{translate("File Name")}</span>

            <span className='max-w-[12ch] truncate text-ellipsis text-xs'>{file.name}</span>
          </div>
          <Tooltip>
            <TooltipTrigger>
              <div className='flex flex-col'>
                <span className='text-xs'>{translate("Uploaded")}</span>
                <span className='text-xs'>
                  {isHebrew ? formatDate_he(file.created_at) : formatDate(file.created_at)}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent className='flex items-center gap-1'>
              <span>By</span>
              <div>
                <img
                  alt=''
                  src={
                    file?.user?.user_picture?.path
                      ? file?.user?.user_picture?.url
                      : `${process.env.PUBLIC_URL}/vector1.svg`
                  }
                  className='h-5 w-5 rounded-full'
                />
              </div>
              <h4>{file.user?.name}</h4>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  )
}
